import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { io, type Socket } from "socket.io-client";
import { useAuth } from "./useAuth";
import { showNotificationToast } from "@/components/notifications/NotificationToast";
import {
  notificationsQueryKey,
  unreadCountQueryKey,
  type UserAlert,
} from "./useUserAlerts";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4003";

type NotificationNewEvent = UserAlert;

// Module-scoped singleton — guards against StrictMode double-mount and avoids
// reconnecting on every render. Keyed by user.id so a user switch or
// sign-out causes a clean teardown via the effect cleanup.
let activeSocket: Socket | null = null;
let activeUserId: string | null = null;

export function useNotificationSocket(): void {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;

    if (activeSocket && activeUserId === user.id) return;

    if (activeSocket && activeUserId !== user.id) {
      activeSocket.disconnect();
      activeSocket = null;
      activeUserId = null;
    }

    // API_URL is "" in the same-origin setup (Caddy/Vite proxy /socket.io to the
    // API), and socket.io cannot parse an empty URL. Lead with long-polling and
    // upgrade only if the path in front of us allows it.
    const socket = io(API_URL || window.location.origin, {
      withCredentials: true,
      transports: ["polling", "websocket"],
      path: "/socket.io",
    });
    activeSocket = socket;
    activeUserId = user.id;

    socket.on("connect", () => {
      console.warn("[notifications] socket connected", socket.id);
    });

    socket.on("notification:new", (event: NotificationNewEvent) => {
      console.warn("[notifications] notification:new", event);
      // Prepend into every cached notifications query for this user; dedupe by id.
      queryClient.setQueriesData<UserAlert[]>(
        { queryKey: ["notifications", user.id] },
        (prev) => {
          if (!prev) return [event];
          if (prev.some((a) => a.id === event.id)) return prev;
          return [event, ...prev];
        },
      );
      queryClient.invalidateQueries({ queryKey: unreadCountQueryKey(user.id) });
      // Also invalidate the include_read=false slice so the dropdown's count
      // matches even before the websocket payload lands in cache.
      queryClient.invalidateQueries({ queryKey: notificationsQueryKey(user.id, false) });

      // Use the dedicated incoming-notification toast — a wider card with an
      // accent stripe, bell avatar, pulsing "new" dot and "View" action.
      // Visually distinct from `toast.info/success/error` which fire as
      // compact strips for action confirmations.
      showNotificationToast(event);
    });

    socket.on("connect_error", (err) => {
      console.warn("[notifications] socket connect_error:", err.message);
    });

    return () => {
      socket.off("notification:new");
      socket.off("connect_error");
      socket.disconnect();
      if (activeSocket === socket) {
        activeSocket = null;
        activeUserId = null;
      }
    };
  }, [user?.id, queryClient]);
}
